/**
 * Auth state management — token storage, login/logout, token refresh.
 * Uses localStorage for persistence across page reloads.
 */

import { apiClient } from './api';
import type {
  LoginRequest,
  LoginResponse,
  LoginTokensResponse,
  MfaConfirmResponse,
  MfaEnrollResponse,
  MfaRequiredResponse,
  OrgSessionResponse,
  RefreshResponse,
  User,
} from '@/types';

const TOKEN_KEY = 'cluster_access_token';
const REFRESH_KEY = 'cluster_refresh_token';
const USER_KEY = 'cluster_user';

// ── Token helpers ───────────────────────────────────────────────────────────

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function getUser(): User | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

export function isAuthenticated(): boolean {
  return !!getToken();
}

/**
 * Check if the current JWT token is expiring soon.
 * @param thresholdSeconds - How many seconds before expiration to consider "expiring soon"
 * @returns true if token expires within thresholdSeconds, or if token is invalid/missing
 */
export function isTokenExpiringSoon(thresholdSeconds = 60): boolean {
  const token = getToken();
  if (!token) return true;

  try {
    // Decode JWT payload (base64url → JSON)
    const parts = token.split('.');
    if (parts.length !== 3) return true;

    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const exp = payload.exp;
    if (!exp) return true;

    const now = Math.floor(Date.now() / 1000);
    return exp - now < thresholdSeconds;
  } catch {
    return true;
  }
}

// ── Auth actions ────────────────────────────────────────────────────────────

/** True when the response carries a real session (legacy login / MFA completion). */
export function isLoginTokensResponse(data: LoginResponse): data is LoginTokensResponse {
  return 'accessToken' in data && 'refreshToken' in data;
}

/** Type guard for the MFA intermediate login response (no tokens present). */
export function isMfaRequiredResponse(data: LoginResponse): data is MfaRequiredResponse {
  return (data as MfaRequiredResponse).mfaRequired === true && !isLoginTokensResponse(data);
}

/** True when the challenge means the factor is not enrolled yet. */
export function isMfaSetupRequired(data: MfaRequiredResponse): boolean {
  return data.status === 'NOT_ENROLLED';
}

export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>('/auth/login', credentials);
  // MFA-required branch returns no tokens — nothing to store; the caller must
  // complete the flow via `verifyMfaLogin` / `enrollMfa` + `confirmMfaEnrollment`.
  if (isLoginTokensResponse(data)) {
    setTokens(data.accessToken, data.refreshToken);
  }
  return data;
}

/**
 * PUT /auth/mfa/enroll — starts TOTP enrollment for a pending (pre-auth) login.
 * Body field is `preAuthJwt` EXACTLY (the backend DTO is strict — any other
 * key is rejected). The returned `otpauthUri` embeds the shared secret and
 * `recoveryCodes` are shown once: keep all of it in memory only.
 */
export async function enrollMfa(preAuthJwt: string): Promise<MfaEnrollResponse> {
  const { data } = await apiClient.put<MfaEnrollResponse>('/auth/mfa/enroll', { preAuthJwt });
  return data;
}

/**
 * POST /auth/mfa/confirm — one step of the two-sequential-code enrollment
 * confirmation, using the CURRENT single-use challenge (`preAuthJwt` exact
 * field). First code → `{ confirmed: false, nextPreAuthJwt }` (no session);
 * second sequential code → `{ confirmed: true, ...tokens }` minted by
 * auth-service. Persist tokens ONLY on the confirmed:true branch, via
 * `adoptSession` in the caller — never store the challenge or codes.
 */
export async function confirmMfaEnrollment(
  preAuthJwt: string,
  code: string,
): Promise<MfaConfirmResponse> {
  const { data } = await apiClient.post<MfaConfirmResponse>('/auth/mfa/confirm', {
    preAuthJwt,
    code,
  });
  return data;
}

/**
 * POST /auth/login/mfa — verifies the TOTP code (or a single-use recovery
 * code) for an already-enrolled user. Body field is `preAuthJwt` EXACTLY.
 * Same success response as the legacy login path (`LoginTokensResponse`).
 */
export async function verifyMfaLogin(
  preAuthJwt: string,
  code: string,
): Promise<LoginTokensResponse> {
  const { data } = await apiClient.post<LoginTokensResponse>('/auth/login/mfa', {
    preAuthJwt,
    code,
  });
  return data;
}

/** POST /auth/login/mfa variant using a recovery code instead of the TOTP. */
export async function verifyMfaLoginWithRecovery(
  preAuthJwt: string,
  recoveryCode: string,
): Promise<LoginTokensResponse> {
  const { data } = await apiClient.post<LoginTokensResponse>('/auth/login/mfa', {
    preAuthJwt,
    recoveryCode,
  });
  return data;
}

/** Persists a completed session (used after MFA steps mint real tokens). */
export function adoptSession(tokens: { accessToken: string; refreshToken: string }): void {
  setTokens(tokens.accessToken, tokens.refreshToken);
}

/**
 * Decode the JWT payload WITHOUT verification — display metadata only
 * (org name/id hint in the header selector, `exp` checks in
 * `isTokenExpiringSoon`). NEVER use this for an authorization decision:
 * the client cannot validate the RS256 signature, and the server
 * re-derives the org scope on every request/refresh.
 * Returns null for missing/invalid tokens.
 */
export function decodeJwtClaims(token: string | null): Record<string, unknown> | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))) as Record<
      string,
      unknown
    >;
  } catch {
    return null;
  }
}

/**
 * POST /auth/sessions/org — exchange the current verified session for an
 * org-scoped session. Body field is `orgId` EXACTLY. The server performs the
 * authorization check; a 401/403 here means access was refused and the
 * caller MUST keep the existing session untouched (this function stores
 * nothing). Only on success may the returned pair be adopted.
 * ADDITIVE: optional `workspaceId` narrows the scope to a workspace WITHIN
 * the org (server verifies membership before minting; 404 if absent/foreign).
 */
export async function switchOrgSession(
  orgId: string,
  workspaceId?: string,
): Promise<OrgSessionResponse> {
  const { data } = await apiClient.post<OrgSessionResponse>('/auth/sessions/org', {
    orgId,
    ...(workspaceId ? { workspaceId } : {}),
  });
  return data;
}

/**
 * POST /auth/sessions/org/logout — end an org-session refresh family
 * precisely (its current refresh token). Used before adopting a NEW org
 * family so repeated switches do not accumulate orphan refresh-token
 * families. The server only accepts tokens belonging to the caller's own
 * org-session families; errors are for callers to swallow (best-effort).
 */
export async function logoutOrgSessionFamily(refreshToken: string): Promise<void> {
  await apiClient.post('/auth/sessions/org/logout', { refreshToken });
}

export async function refreshToken(): Promise<RefreshResponse> {
  const currentRefresh = getRefreshToken();
  if (!currentRefresh) throw new Error('No refresh token');

  const { data } = await apiClient.post<RefreshResponse>('/auth/refresh', {
    refreshToken: currentRefresh,
  });
  setTokens(data.accessToken, data.refreshToken);
  return data;
}

export async function fetchCurrentUser(): Promise<User> {
  const { data } = await apiClient.get<User>('/auth/me');
  // GET /auth/me returns `{ id, ... }` (no `_id`); normalize so downstream
  // consumers can rely on `user._id` (the `User` type uses `_id`).
  const user = { ...data, _id: (data as any)._id ?? (data as any).id } as User;
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  return user;
}

export async function logout(): Promise<void> {
  const currentRefresh = getRefreshToken();
  try {
    if (currentRefresh) {
      await apiClient.post('/auth/logout', { refreshToken: currentRefresh });
    }
  } catch {
    // Ignore logout API errors — clear local state regardless
  } finally {
    clearAuth();
  }
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
}
